/**
 * @file thunks.js
 * @fileoverview API functions which are called by the jobs redux thunks.
 *
 * @typedef {import('BMapsSrc/jobman').JobStatusOptions} JobStatusOptions
 * @typedef {import('BMapsSrc/jobman').JobTypeOptions} JobTypeOptions
 * @typedef {import('BMapsSrc/jobman').postJobResponse} postJobResponse
 * @typedef {import('BMapsSrc/jobman').getJobsResponse} getJobsResponse
 * @typedef {import('BMapsSrc/jobman').deleteJobResponse} deleteJobResponse
 * @typedef {import('BMapsSrc/jobman').loadJobResponse} loadJobResponse
 *
 */
import {
    deleteJob, getJobs, loadJobResult, jobClassList,
} from 'BMapsSrc/jobman';
import * as jobActions from '.';

/**
 * @returns {{ results?: getJobsResponse, error?: string }}
 */
export const fetchJobs = () => async (dispatch) => {
    const jobs = await getJobs();
    if (!jobs.error && jobs.results) {
        dispatch(jobActions.loadJobs(jobs.results));
        return { results: jobs.results };
    }
    // A failed request and a missing login are each recorded for what they are: no login means
    // nothing was asked of the service, not that it is down. A success that carried no results
    // says nothing either way, so it leaves the last answer standing.
    if (jobs.reason) dispatch(jobActions.loadJobsFailed(jobs.reason));
    return { error: jobs.error };
};

/**
 *
 * @param {JobTypeOptions} jobType
 * @param {Object} data
 * @param {Object} stash
 *
 * @returns {{ results?: postJobResponse, error?: string }}
 */
export const createJob = (jobType, data, stash) => async (dispatch) => {
    const JobClass = jobClassList[jobType];
    if (!JobClass) return { error: `Unrecognized job type: ${jobType}` };
    const job = new JobClass({});
    const jobResult = await job.sendJobCreationRequest(data, stash);
    if (!jobResult.error && jobResult.results) {
        dispatch(jobActions.addJob(jobResult.results));
        return { results: jobResult.results };
    }
    return { error: jobResult.error, results: jobResult.results };
};

/**
 * @param {string} jobid
 * @returns {{ results?: deleteJobResponse, error?: string }}
 */
export const removeJob = (jobid) => async (dispatch, getState) => {
    const job = getState().jobs.jobs.find((j) => j.jobid === jobid);
    if (!job) return { error: `The requested job could not be found: ${jobid}` };
    const { results, error } = await deleteJob(jobid);
    if (error) return dispatch(setJobError(jobid, error));
    if (!results || !results.success) return dispatch(setJobError(jobid, 'Failed to delete job.'));
    dispatch(jobActions.removeJob(jobid));

    return { results };
};

/**
 *
 * @param {string} jobid
 * @param {object} options
 * @returns {{ results?: loadJobResponse, error?: string }}
 */
export const loadResult = (jobid, options=null) => async (dispatch, getState) => {
    let { results, error } = await loadJobResult(jobid);
    const job = getState().jobs.jobs.find((j) => j.jobid === jobid);
    if (!job) return { error: `The requested job could not be found: ${jobid}` };
    // Update the local job based on response.
    dispatch(jobActions.setJob({
        ...job,
        delivered: new Date().toISOString(),
        state: results?.state || job.state, // allow job state override from loadJobResult packet
    }));
    if (error) return dispatch(setJobError(jobid, error));
    const JobClass = jobClassList[job.jobtype];
    if (!JobClass) return dispatch(setJobError(jobid, `Unrecognized job type: ${job.jobtype}`));
    const jobDefinition = new JobClass({ jobId: job.jobid });
    ({ results, error } = await jobDefinition.integrateResults(job, results, options));
    if (error) return dispatch(setJobError(jobid, error));

    return { results };
};

/**
 * @param {string} jobid
 * @returns {{ results?: loadJobResponse, error?: string }}
 */
export const updateJobErrors = (jobid) => async (dispatch, getState) => {
    const { results, error } = await loadJobResult(jobid);
    const job = getState().jobs.jobs.find((j) => j.jobid === jobid);
    if (!job) return { error: `The requested job could not be found: ${jobid}` };
    dispatch(jobActions.setJob({ ...job, delivered: new Date().toISOString() }));
    // currently only errors are added to localJobState
    // once more is needed this can be updated
    if (error) dispatch(setJobError(jobid, error));
    return { results };
};

/**
 * @param {string} jobid
 * @param {string} error
 * @returns {{ error: string }}
 */
const setJobError = (jobid, error) => (dispatch) => {
    dispatch(jobActions.updateLocalJobState({ jobid, error }));
    return { error };
};
